import React, { useState } from 'react';
import { Star, Wrench, Rocket, BarChart3, Sparkles, Download, X, Loader2, Layers, FileCheck } from 'lucide-react';
import { createResumeScorerPdf } from '../../utils/resumeScorerPdf';
import './ResumeScorerResults.css';

const ResumeScorerResults = ({
  scorerResult,
  setScorerResult,
  onClose,
  onDownloadPdf,
  studentProfile,
  locked = false
}) => {
  const [downloading, setDownloading] = useState(false);

  if (!scorerResult) return null;

  const handleClose = () => {
    if (onClose) onClose();
    if (setScorerResult) setScorerResult(null);
  };

  const handleDownload = async () => {
    if (onDownloadPdf) {
      onDownloadPdf();
      return;
    }
    try {
      setDownloading(true);
      await createResumeScorerPdf(scorerResult, studentProfile);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="card scorer-results-card">
      {/* Top right modal close button */}
      <button
        type="button"
        className="scorer-modal-close-btn"
        onClick={handleClose}
        aria-label="Close Results"
        title="Close"
      >
        <X size={18} />
      </button>

      <div className="scorer-header">
        <div className="score-display">
          <div className="score-value">
            {scorerResult.score}
            <span style={{ fontSize: '0.95rem', fontWeight: 600, opacity: 0.85 }}> / 100</span>
          </div>
          <div className="score-label">Score</div>
          {scorerResult.creative_analysis?.persona && (
            <div className="persona-badge">{scorerResult.creative_analysis.persona}</div>
          )}
        </div>
        <div className="score-summary">
          <div className="industry-fit-box">
             <span className="industry-fit-label">Industry Fit:</span>
             <span className="industry-fit-value">{scorerResult.creative_analysis?.industry_fit || 'N/A'}%</span>
             {scorerResult.evaluated_at && (
               <span style={{ marginLeft: '12px', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                 Assessed on {new Date(scorerResult.evaluated_at).toLocaleDateString()}
               </span>
             )}
          </div>
          <h3>Analysis Summary</h3>
          <p>{scorerResult.summary}</p>
        </div>
      </div>

      {locked && (
        <div
          style={{
            marginTop: '14px',
            padding: '10px 14px',
            borderRadius: '8px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            color: '#166534',
            fontSize: '12.5px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FileCheck size={15} color="#15803d" />
          <span>
            This resume version has already been evaluated. Upload a modified resume to unlock a new evaluation.
          </span>
        </div>
      )}

      {scorerResult.creative_analysis?.pro_tip && (
        <div className="pro-tip-card">
          <div className="pro-tip-header">
            <Sparkles size={16} className="sparkle" /> AI PRO-TIP
          </div>
          <p>{scorerResult.creative_analysis.pro_tip}</p>
        </div>
      )}

      {scorerResult.rubric_breakdown && (
        <div className="rubric-breakdown-section" style={{ marginTop: '20px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
            <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', color: '#1e293b' }}>
              <Layers size={19} color="#3b82f6" />
              100-Point Evidence Rubric Breakdown
            </h4>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '3px 10px', borderRadius: '12px' }}>
              Deterministic Total: {scorerResult.score} / 100
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
            {Object.entries(scorerResult.rubric_breakdown).map(([key, item]) => {
              const maxPoints = item.max_points || (key === 'quantifiable_impact' || key === 'technical_depth' ? 25 : key === 'experience_relevance' ? 20 : 15);
              const score = typeof item.score === 'number' ? item.score : 0;
              const pct = Math.min(100, Math.max(0, (score / maxPoints) * 100));
              const label = item.label || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

              return (
                <div key={key} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{label}</span>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#2563eb' }}>
                      {score.toFixed(1)} / {maxPoints.toFixed(1)}
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: score > 0 ? '#3b82f6' : '#cbd5e1', transition: 'width 0.3s' }} />
                  </div>
                  {item.evidence && (
                    <div style={{ fontSize: '11.5px', color: '#64748b', lineHeight: '1.4', background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', borderLeft: '2px solid #3b82f6' }}>
                      {item.evidence}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {scorerResult.skill_map && (
        <div className="skill-map-section">
          <h4><BarChart3 size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Skill Proficiency Map</h4>
          <div className="skill-grid">
            {scorerResult.skill_map.map((skill, i) => (
              <div key={i} className="skill-item">
                <div className="skill-info">
                  <span>{skill.category}</span>
                  <span>{skill.score}/10</span>
                </div>
                <div className="skill-bar-bg">
                  <div 
                    className="skill-bar-fill" 
                    style={{ width: `${(skill.score / 10) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="scorer-details">
        <div className="scorer-col">
          <h4><Star size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Strengths</h4>
          <ul>
            {scorerResult.strengths?.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
        <div className="scorer-col">
          <h4><Wrench size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Improvements</h4>
          <ul className="improvement-list">
            {scorerResult.improvements?.map((imp, i) => (
              <li key={i} className="improvement-item">
                <div className="suggestion">{typeof imp === 'string' ? imp : imp.suggestion}</div>
                {imp.reason && <div className="reason">Why: {imp.reason}</div>}
                
                {imp.original_sentence && (
                  <div className="rewrite-container">
                    <div className="before-box">
                      <strong>Original:</strong>
                      <p>"{imp.original_sentence}"</p>
                    </div>
                    <div className="after-box">
                      <strong>Suggested Rewrite:</strong>
                      <p>"{imp.rewrite_example}"</p>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {scorerResult.career_paths && (
        <div className="career-paths">
          <h4><Rocket size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Career Paths</h4>
          <div className="path-chips">
            {scorerResult.career_paths.map((p, i) => (
              <span key={i} className="path-chip">{p}</span>
            ))}
          </div>
        </div>
      )}
      
      {/* Bottom Actions Row */}
      <div className="scorer-action-row">
        <button
          type="button"
          className="scorer-download-btn"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? (
            <>
              <Loader2 size={16} className="scorer-spinner" /> Generating PDF...
            </>
          ) : (
            <>
              <Download size={16} /> Download PDF Report
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ResumeScorerResults;
